import React, { createContext, FC, useContext, useEffect, useMemo, useState } from 'react';
import MdKeyboardArrowDown from 'src/shared/material_icons/hardware/keyboard_arrow_down.svg';
import MdKeyboardArrowRight from 'src/shared/material_icons/hardware/keyboard_arrow_right.svg';
import styled from 'styled-components';
import { Icon } from '../icon';

export interface SidebarTreeItem {
    id: number | string;
    content: React.ReactNode;
    items?: SidebarTreeItem[] | null;
    opened?: boolean;
    notOpenOnClick?: boolean;
    customStyle?: React.CSSProperties;
    selected?: boolean;
}

const TreeContainer = styled.div``;

const TreeItemContainer = styled.div<{ $selected?: boolean }>`
    background-color: transparent;
`;

const TreeItemHeader = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;

    ${Icon} {
        width: 20px;
        height: 20px;
        flex: 0 0 auto;
    }
`;

const ItemHeader = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;

const TreeItemSubItems = styled.div`
    padding-left: 25px;
`;

// Opened items context
interface Context {
    openedItems: (number | string)[];
    setOpenedItems: (ids: (number | string)[]) => void;
}

const treeContext = createContext<Context>({
    openedItems: [],
    setOpenedItems: () => null
});

const Item: FC<{ item: SidebarTreeItem; customStyle?: React.CSSProperties }> = ({ item, customStyle }) => {
    const ctx = useContext(treeContext);
    const isOpened = useMemo(() => ctx.openedItems.indexOf(item.id) !== -1, [ctx.openedItems]);

    const handleOpenClick = () => {
        if (!isOpened) ctx.setOpenedItems([...ctx.openedItems, item.id]);
        else ctx.setOpenedItems(ctx.openedItems.filter((x) => x !== item.id));
    };

    return (
        <TreeItemContainer $selected={item.selected}>
            <TreeItemHeader>
                {item.items && item.items.length > 0 && (
                    <Icon $hoverable>{!isOpened ? <MdKeyboardArrowRight onClick={handleOpenClick} /> : <MdKeyboardArrowDown onClick={handleOpenClick} />}</Icon>
                )}

                {item.items && item.items.length > 0 && <ItemHeader onClick={!item.notOpenOnClick ? handleOpenClick : undefined}>{item.content}</ItemHeader>}

                {(item.id == '' || item.items == null || (item.items && item.items.length == 0)) && <ItemHeader>{item.content}</ItemHeader>}
            </TreeItemHeader>

            {item.items && item.items.length > 0 && isOpened && (
                <TreeItemSubItems>
                    {item.items.map((item) => (
                        <Item key={item.id} item={item} />
                    ))}
                </TreeItemSubItems>
            )}
        </TreeItemContainer>
    );
};

interface SidebarTreeProps {
    items: SidebarTreeItem[];
    customStyle?: React.CSSProperties;
}

export const CollapsibleTreeItems: FC<SidebarTreeProps> = ({ items, customStyle }) => {
    const [openedItems, setOpenedItems] = useState<(number | string)[]>([
        ...items.filter((x) => x.opened).map((x) => x.id),
        ...items
            .flatMap((x) => x.items)
            .filter((x) => x?.opened)
            .map((x) => x!.id)
    ]);

    function extractSelectedIds(item: SidebarTreeItem, parentIds: (string | number)[] = []): (string | number)[] {
        const result = [];

        if (item.selected) {
            result.push(...parentIds, item.id);
        }

        if (item.items) {
            const updatedParents = [...parentIds, item.id];
            for (const subItem of item.items) {
                result.push(...extractSelectedIds(subItem, updatedParents));
            }
        }

        return result;
    }

    useEffect(() => {
        const selectedIds = items.flatMap((item) => extractSelectedIds(item));
        setOpenedItems((prev) => [...prev, ...selectedIds]);
    }, [items]);

    // useEffect(() => {
    //     setOpenedItems([...items.filter(x => x.opened).map(x => x.id), ...items.flatMap(x => x.items).filter(x => x?.opened).map(x => x!.id)]);
    // }, [items])

    return (
        <treeContext.Provider value={{ openedItems, setOpenedItems }}>
            <TreeContainer style={customStyle}>
                {items.map((item) => (
                    <Item key={item.id} item={item} customStyle={customStyle} />
                ))}
            </TreeContainer>
        </treeContext.Provider>
    );
};
